import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { brl } from "../lib";
import { toastSuccess, toastError, pickErrorMsg } from "../lib/feedback";
import { AlertTriangle, Plus, Trash2, Edit3, Camera, X, ChevronRight, History } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Paleta consistente com ServicosPanel
const ink = "#f5f0e8";
const ink2 = "#cdc8be";
const ink3 = "#8a857a";
const gold = "#b8935a";
const line = "#2b2833";
const bg2 = "#15141a";
const bg1 = "#0d0d0f";
const danger = "#c47a7a";
const dangerBg = "rgba(196,122,122,0.08)";

const lbl = { display: "block", fontSize: 10, color: ink3, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 5 };
const inp = {
  width: "100%", background: bg1, border: `1px solid ${line}`,
  color: ink, padding: "8px 10px", fontSize: 13, borderRadius: 3, boxSizing: "border-box",
};
const btnGhost = {
  background: "transparent", color: ink2, border: `1px solid ${line}`,
  padding: "8px 16px", fontSize: 12, letterSpacing: 0.5, cursor: "pointer",
};
const btnPrimary = {
  background: gold, color: bg1, border: "none",
  padding: "9px 20px", fontSize: 12, fontWeight: 600, letterSpacing: 0.5, cursor: "pointer",
};
const btnDanger = { ...btnGhost, color: danger, borderColor: "rgba(139,58,58,0.5)" };

const RESP_TIPOS = [
  { v: "producao", label: "Produção / Terceirizado" },
  { v: "colocacao", label: "Colocação" },
  { v: "frete", label: "Frete" },
  { v: "medicao", label: "Medição" },
  { v: "material", label: "Material / Fornecedor" },
  { v: "atraso", label: "Atraso / Camasa" },
  { v: "obra", label: "Obra / Empreiteiro" },
];

const STATUS_OC = {
  aberto: { label: "Aberto", color: "#c47a7a" },
  em_negociacao: { label: "Em negociação", color: "#c9a961" },
  resolvido: { label: "Resolvido", color: "#7da882" },
};

const STATUS_SIN = {
  aberto: { label: "Aberto", color: "#c47a7a" },
  notificado: { label: "Notificado", color: "#c9a961" },
  aceito: { label: "Aceito", color: "#8a9fbc" },
  contestado: { label: "Contestado", color: "#c47a7a" },
  acordo_parcial: { label: "Acordo Parcial", color: "#c9a961" },
  quitado: { label: "Quitado", color: "#7da882" },
};

function fmtDataBR(iso) {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  return `${d}/${m}/${y}`;
}

function StatusPill({ status, map }) {
  const s = map[status] || { label: status, color: ink3 };
  return (
    <span style={{
      fontSize: 10, letterSpacing: 1.2, textTransform: "uppercase",
      color: s.color, border: `1px solid ${s.color}`,
      padding: "3px 9px", borderRadius: 2, fontWeight: 600,
    }}>{s.label}</span>
  );
}

/**
 * Painel de Ocorrências / Sinistros.
 *
 * @param {string} obraId - Se passado, mostra apenas ocorrências dessa obra (modo "aba")
 *                          Se ausente, é o modo página completa (menu principal)
 */
export default function OcorrenciasPanel({ obraId = null }) {
  const [items, setItems] = useState([]);
  const [obras, setObras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [filters, setFilters] = useState({
    obra_id: obraId || "",
    status: "",
    tipo_responsavel: "",
    data_inicio: "",
    data_fim: "",
  });

  const token = typeof window !== "undefined" ? localStorage.getItem("cps_token") : null;
  const headers = useMemo(() => token ? { Authorization: `Bearer ${token}` } : {}, [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const url = obraId ? `${API}/ocorrencias/obra/${obraId}` : `${API}/ocorrencias`;
      const r = await axios.get(url, { headers, params: obraId ? undefined : params });
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toastError(pickErrorMsg(e));
    } finally {
      setLoading(false);
    }
  }, [filters, headers, obraId]);

  const loadObras = useCallback(async () => {
    if (obraId) return;
    try {
      const r = await axios.get(`${API}/obras`, { headers });
      const list = Array.isArray(r.data) ? r.data : r.data?.items || [];
      setObras(list.filter(o => o.status !== "arquivada"));
    } catch { /* silent */ }
  }, [headers, obraId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadObras(); }, [loadObras]);

  const totalPendente = useMemo(() => {
    return items.reduce((s, oc) => {
      const sinistros = oc.sinistros || [];
      return s + sinistros.reduce((ss, sn) => ss + (Number(sn.saldo_devedor) || 0), 0);
    }, 0);
  }, [items]);

  return (
    <div style={{ padding: obraId ? 0 : "24px 32px", color: ink }} data-testid="ocorrencias-panel">
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 22 }}>
        <div>
          <div style={{ fontSize: 10, color: ink3, letterSpacing: 2, textTransform: "uppercase", marginBottom: 4 }}>
            Módulo de Sinistros
          </div>
          <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 28, margin: 0, color: ink }}>
            {obraId ? "Ocorrências desta obra" : "Ocorrências"}
          </h2>
        </div>
        <button
          onClick={() => setShowForm(true)}
          style={btnPrimary}
          data-testid="btn-nova-ocorrencia"
        >
          <Plus size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />
          Nova Ocorrência
        </button>
      </div>

      {/* KPIs (só na página completa) */}
      {!obraId && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12, marginBottom: 22 }}>
          <Kpi label="Total de Ocorrências" value={items.length} testid="kpi-total" />
          <Kpi label="Abertas" value={items.filter(o => o.status === "aberto").length} testid="kpi-abertas" tone="danger" />
          <Kpi label="Em Negociação" value={items.filter(o => o.status === "em_negociacao").length} testid="kpi-neg" tone="warn" />
          <Kpi label="Sinistros Pendentes" value={brl(totalPendente)} testid="kpi-pendente" tone="gold" />
        </div>
      )}

      {/* Filtros (só na página completa) */}
      {!obraId && (
        <div style={{ background: bg2, border: `1px solid ${line}`, borderRadius: 4, padding: 14, marginBottom: 18, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 10 }} data-testid="filtros">
          <div>
            <label style={lbl}>Obra</label>
            <select value={filters.obra_id} onChange={e => setFilters(f => ({ ...f, obra_id: e.target.value }))} style={inp} data-testid="filtro-obra">
              <option value="">Todas</option>
              {obras.map(o => <option key={o.id} value={o.id}>{o.codigo} — {o.nome}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Status</label>
            <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} style={inp} data-testid="filtro-status">
              <option value="">Todos</option>
              {Object.entries(STATUS_OC).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Tipo de responsável</label>
            <select value={filters.tipo_responsavel} onChange={e => setFilters(f => ({ ...f, tipo_responsavel: e.target.value }))} style={inp} data-testid="filtro-tipo">
              <option value="">Todos</option>
              {RESP_TIPOS.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>De</label>
            <input type="date" value={filters.data_inicio} onChange={e => setFilters(f => ({ ...f, data_inicio: e.target.value }))} style={inp} data-testid="filtro-de" />
          </div>
          <div>
            <label style={lbl}>Até</label>
            <input type="date" value={filters.data_fim} onChange={e => setFilters(f => ({ ...f, data_fim: e.target.value }))} style={inp} data-testid="filtro-ate" />
          </div>
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: ink3 }}>Carregando…</div>
      ) : items.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: ink3, background: bg2, border: `1px dashed ${line}` }} data-testid="empty-state">
          Nenhuma ocorrência registrada{obraId ? " nesta obra" : ""}.
        </div>
      ) : (
        <div style={{ background: bg2, border: `1px solid ${line}`, borderRadius: 4, overflow: "hidden" }} data-testid="lista-ocorrencias">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead style={{ background: bg1 }}>
              <tr>
                <Th>Data</Th>
                {!obraId && <Th>Obra</Th>}
                <Th>Descrição</Th>
                <Th>Responsáveis</Th>
                <Th align="right">Total Sinistro</Th>
                <Th align="right">Pendente</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {items.map(oc => {
                const pend = (oc.sinistros || []).reduce((s, x) => s + (Number(x.saldo_devedor) || 0), 0);
                const obra = obras.find(o => o.id === oc.obra_id);
                return (
                  <tr key={oc.id} style={{ borderTop: `1px solid ${line}` }} data-testid={`row-${oc.id}`}>
                    <Td>{fmtDataBR(oc.data_ocorrencia)}</Td>
                    {!obraId && <Td small>{obra ? `${obra.codigo}` : oc.obra_id.slice(0, 8)}</Td>}
                    <Td max={260}>{oc.descricao}</Td>
                    <Td>{(oc.responsaveis || []).map((r, i) => (
                      <span key={i} style={{ fontSize: 11, color: ink2, marginRight: 6 }}>
                        {r.responsavel_nome} <span style={{ color: ink3 }}>({r.percentual_culpa}%)</span>
                      </span>
                    ))}</Td>
                    <Td align="right">{oc.total_sinistro != null ? brl(oc.total_sinistro) : "—"}</Td>
                    <Td align="right" style={{ color: pend > 0 ? danger : ink3, fontWeight: pend > 0 ? 600 : 400 }}>
                      {pend > 0 ? brl(pend) : "—"}
                    </Td>
                    <Td><StatusPill status={oc.status} map={STATUS_OC} /></Td>
                    <Td>
                      <button
                        onClick={() => setOpenId(oc.id)}
                        style={{ ...btnGhost, padding: "4px 10px", fontSize: 11 }}
                        data-testid={`btn-open-${oc.id}`}
                      >
                        Ver <ChevronRight size={11} style={{ verticalAlign: "middle" }} />
                      </button>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal: Novo formulário */}
      {showForm && (
        <FormOcorrencia
          obras={obras}
          fixedObraId={obraId}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
          headers={headers}
        />
      )}

      {/* Modal: Detalhe */}
      {openId && (
        <DetalheOcorrencia
          id={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => load()}
          headers={headers}
          obras={obras}
        />
      )}
    </div>
  );
}

/* --------------------- HELPERS --------------------- */

function Kpi({ label, value, tone = "default", testid }) {
  const bgTone = tone === "gold" ? "rgba(184,147,90,0.08)"
    : tone === "danger" ? "rgba(196,122,122,0.08)"
    : tone === "warn" ? "rgba(201,169,97,0.06)" : bg2;
  const border = tone === "gold" ? "rgba(184,147,90,0.4)"
    : tone === "danger" ? "rgba(196,122,122,0.4)"
    : tone === "warn" ? "rgba(201,169,97,0.3)" : line;
  const colorV = tone === "gold" ? gold
    : tone === "danger" ? danger
    : tone === "warn" ? "#c9a961" : ink;
  return (
    <div style={{ background: bgTone, border: `1px solid ${border}`, borderRadius: 4, padding: "14px 16px" }} data-testid={testid}>
      <div style={{ fontSize: 10, color: ink3, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontFamily: "Fraunces, serif", color: colorV }}>{value}</div>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return <th style={{ padding: "12px 14px", fontSize: 10, textAlign: align, color: ink3, letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 600 }}>{children}</th>;
}

function Td({ children, align = "left", small, max, style }) {
  return <td style={{
    padding: "12px 14px", textAlign: align,
    color: small ? ink3 : ink,
    fontSize: small ? 11 : 13,
    maxWidth: max, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: max ? "nowrap" : "normal",
    ...(style || {}),
  }}>{children}</td>;
}

/* --------------------- FORM (Criar/Editar) --------------------- */

function FormOcorrencia({ obras, fixedObraId, onClose, onSaved, headers, initial = null }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(() => initial ? {
    ...initial,
    responsaveis: initial.responsaveis || [],
    fotos: initial.fotos || [],
    tem_desconto: (initial.desconto_cliente || 0) > 0,
  } : {
    obra_id: fixedObraId || "",
    numero_pedido: "",
    data_ocorrencia: new Date().toISOString().slice(0, 10),
    descricao: "",
    material: "",
    fotos: [],
    responsaveis: [{ responsavel_tipo: "frete", responsavel_nome: "", percentual_culpa: 100 }],
    custo_correcao: 0,
    desconto_cliente: 0,
    camasa_absorve: 0,
    tem_desconto: false,
    status: "aberto",
  });
  const [motivo, setMotivo] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const somaCulpa = useMemo(
    () => (form.responsaveis || []).reduce((s, r) => s + (Number(r.percentual_culpa) || 0), 0),
    [form.responsaveis],
  );

  const camasaAbsorveCalc = useMemo(() => {
    const pctCamasa = (form.responsaveis || [])
      .filter(r => r.responsavel_tipo === "atraso")
      .reduce((s, r) => s + (Number(r.percentual_culpa) || 0), 0);
    return Math.round((Number(form.custo_correcao) || 0) * pctCamasa / 100 * 100) / 100;
  }, [form.responsaveis, form.custo_correcao]);

  // Só atualiza camasa_absorve automaticamente se usuário não editou manualmente
  const [camasaEditado, setCamasaEditado] = useState(false);
  useEffect(() => {
    if (!camasaEditado) setForm(f => ({ ...f, camasa_absorve: camasaAbsorveCalc }));
  }, [camasaAbsorveCalc, camasaEditado]);

  const totalSinistro = useMemo(() => {
    const cc = Number(form.custo_correcao) || 0;
    const dc = (form.tem_desconto ? Number(form.desconto_cliente) : 0) || 0;
    const ca = Number(form.camasa_absorve) || 0;
    return Math.round((cc + dc - ca) * 100) / 100;
  }, [form.custo_correcao, form.desconto_cliente, form.camasa_absorve, form.tem_desconto]);

  const previewSinistros = useMemo(() => {
    return (form.responsaveis || [])
      .filter(r => r.responsavel_tipo !== "atraso" && r.responsavel_nome)
      .map(r => ({
        nome: r.responsavel_nome,
        pct: Number(r.percentual_culpa) || 0,
        valor: Math.round(totalSinistro * (Number(r.percentual_culpa) || 0) / 100 * 100) / 100,
      }));
  }, [form.responsaveis, totalSinistro]);

  const addResp = () => setForm(f => ({
    ...f,
    responsaveis: [...(f.responsaveis || []), { responsavel_tipo: "frete", responsavel_nome: "", percentual_culpa: 0 }],
  }));

  const removeResp = (idx) => setForm(f => ({
    ...f,
    responsaveis: (f.responsaveis || []).filter((_, i) => i !== idx),
  }));

  const updateResp = (idx, patch) => setForm(f => ({
    ...f,
    responsaveis: (f.responsaveis || []).map((r, i) => i === idx ? { ...r, ...patch } : r),
  }));

  const uploadFotos = async (files) => {
    if (!files?.length || !form.obra_id) {
      toastError("Selecione a obra antes de enviar fotos.");
      return;
    }
    setUploading(true);
    try {
      const novos = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("obra_id", form.obra_id);
        const r = await axios.post(`${API}/ocorrencias/upload-foto`, fd, {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
        });
        novos.push(r.data.url);
      }
      setForm(f => ({ ...f, fotos: [...(f.fotos || []), ...novos] }));
      toastSuccess(`${novos.length} foto(s) enviada(s).`);
    } catch (e) {
      toastError(pickErrorMsg(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeFoto = (idx) => setForm(f => ({ ...f, fotos: (f.fotos || []).filter((_, i) => i !== idx) }));

  const canSubmit = form.obra_id && form.data_ocorrencia && form.descricao
    && Math.abs(somaCulpa - 100) < 0.01
    && (form.responsaveis || []).every(r => r.responsavel_nome && r.responsavel_tipo);

  const submit = async () => {
    if (!canSubmit) {
      toastError("Preencha os campos obrigatórios e verifique a soma dos percentuais (=100%).");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        obra_id: form.obra_id,
        numero_pedido: form.numero_pedido || null,
        data_ocorrencia: form.data_ocorrencia,
        descricao: form.descricao,
        material: form.material || null,
        fotos: form.fotos || [],
        responsaveis: (form.responsaveis || []).map(r => ({
          responsavel_tipo: r.responsavel_tipo,
          responsavel_nome: r.responsavel_nome,
          percentual_culpa: Number(r.percentual_culpa) || 0,
        })),
        custo_correcao: Number(form.custo_correcao) || 0,
        desconto_cliente: form.tem_desconto ? (Number(form.desconto_cliente) || 0) : 0,
        camasa_absorve: Number(form.camasa_absorve) || 0,
        status: form.status || "aberto",
      };
      if (isEdit) {
        payload.motivo_alteracao = motivo;
        await axios.put(`${API}/ocorrencias/${initial.id}`, payload, { headers });
        toastSuccess("Ocorrência atualizada.");
      } else {
        await axios.post(`${API}/ocorrencias`, payload, { headers });
        toastSuccess("Ocorrência registrada.");
      }
      onSaved();
    } catch (e) {
      toastError(pickErrorMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} title={isEdit ? "Editar Ocorrência" : "Nova Ocorrência"} testid="modal-form-ocorrencia">
      {/* Seção 1 */}
      <Section num={1} title="O que aconteceu">
        <div style={grid2}>
          {!fixedObraId && (
            <div>
              <label style={lbl}>Obra *</label>
              <select
                value={form.obra_id}
                onChange={e => setForm(f => ({ ...f, obra_id: e.target.value }))}
                style={inp}
                disabled={isEdit}
                data-testid="form-obra"
              >
                <option value="">— Selecione —</option>
                {obras.map(o => <option key={o.id} value={o.id}>{o.codigo} — {o.nome}</option>)}
              </select>
            </div>
          )}
          <div>
            <label style={lbl}>Número do pedido</label>
            <input value={form.numero_pedido} onChange={e => setForm(f => ({ ...f, numero_pedido: e.target.value }))} style={inp} data-testid="form-pedido" />
          </div>
          <div>
            <label style={lbl}>Data *</label>
            <input type="date" value={form.data_ocorrencia} onChange={e => setForm(f => ({ ...f, data_ocorrencia: e.target.value }))} style={inp} data-testid="form-data" />
          </div>
          <div>
            <label style={lbl}>Material</label>
            <input value={form.material || ""} onChange={e => setForm(f => ({ ...f, material: e.target.value }))} style={inp} placeholder="Ex: Granito Verde Ubatuba" data-testid="form-material" />
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={lbl}>Descrição *</label>
          <textarea
            value={form.descricao}
            onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
            style={{ ...inp, minHeight: 80 }}
            placeholder="Descreva o que aconteceu..."
            data-testid="form-descricao"
          />
        </div>

        <div style={{ marginTop: 12 }}>
          <label style={lbl}>Fotos ({(form.fotos || []).length})</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            {(form.fotos || []).map((url, i) => (
              <div key={i} style={{ position: "relative", width: 90, height: 90, borderRadius: 3, overflow: "hidden", border: `1px solid ${line}` }}>
                <img src={url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <button
                  onClick={() => removeFoto(i)}
                  style={{ position: "absolute", top: 2, right: 2, background: "rgba(0,0,0,0.7)", color: ink, border: "none", padding: 2, cursor: "pointer" }}
                  data-testid={`btn-remove-foto-${i}`}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
          <input
            type="file"
            ref={fileRef}
            multiple
            accept="image/*"
            onChange={e => uploadFotos(Array.from(e.target.files || []))}
            style={{ display: "none" }}
            data-testid="input-fotos"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            style={btnGhost}
            disabled={uploading || !form.obra_id}
            data-testid="btn-upload-foto"
          >
            <Camera size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />
            {uploading ? "Enviando…" : "Enviar Fotos"}
          </button>
          {!form.obra_id && <span style={{ marginLeft: 10, fontSize: 11, color: ink3 }}>Selecione a obra primeiro.</span>}
        </div>
      </Section>

      {/* Seção 2 */}
      <Section num={2} title="Quem causou">
        {(form.responsaveis || []).map((r, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1.4fr 1.8fr 90px 40px", gap: 8, marginBottom: 8 }} data-testid={`resp-${i}`}>
            <select value={r.responsavel_tipo} onChange={e => updateResp(i, { responsavel_tipo: e.target.value })} style={inp} data-testid={`resp-${i}-tipo`}>
              {RESP_TIPOS.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
            <input value={r.responsavel_nome} onChange={e => updateResp(i, { responsavel_nome: e.target.value })} style={inp} placeholder="Nome do responsável" data-testid={`resp-${i}-nome`} />
            <div style={{ position: "relative" }}>
              <input type="number" min="0" max="100" step="0.01" value={r.percentual_culpa} onChange={e => updateResp(i, { percentual_culpa: e.target.value })} style={{ ...inp, paddingRight: 22 }} data-testid={`resp-${i}-pct`} />
              <span style={{ position: "absolute", right: 8, top: 8, fontSize: 12, color: ink3 }}>%</span>
            </div>
            <button
              type="button"
              onClick={() => removeResp(i)}
              style={{ ...btnDanger, padding: "6px 8px" }}
              disabled={(form.responsaveis || []).length <= 1}
              data-testid={`resp-${i}-remove`}
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}
        <button type="button" onClick={addResp} style={btnGhost} data-testid="btn-add-resp">
          <Plus size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />Adicionar responsável
        </button>
        <div style={{ marginTop: 10, fontSize: 12, color: Math.abs(somaCulpa - 100) < 0.01 ? "#7da882" : danger }} data-testid="soma-culpa">
          Soma: <strong>{somaCulpa.toFixed(2)}%</strong> {Math.abs(somaCulpa - 100) < 0.01 ? "✓" : "(precisa somar 100%)"}
        </div>
      </Section>

      {/* Seção 3 */}
      <Section num={3} title="Impacto financeiro">
        <div style={grid2}>
          <div>
            <label style={lbl}>Custo de correção *</label>
            <input type="number" min="0" step="0.01" value={form.custo_correcao} onChange={e => setForm(f => ({ ...f, custo_correcao: e.target.value }))} style={inp} data-testid="form-custo" />
          </div>
          <div>
            <label style={lbl}>Camasa absorve (R$)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.camasa_absorve}
              onChange={e => { setForm(f => ({ ...f, camasa_absorve: e.target.value })); setCamasaEditado(true); }}
              style={inp}
              data-testid="form-camasa-absorve"
            />
            <div style={{ fontSize: 10, color: ink3, marginTop: 3 }}>
              Sugerido: {brl(camasaAbsorveCalc)} {camasaEditado && (
                <button onClick={() => { setCamasaEditado(false); }} style={{ background: "none", color: gold, border: "none", cursor: "pointer", fontSize: 10, textDecoration: "underline" }}>usar cálculo</button>
              )}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10 }}>
          <label style={{ ...lbl, marginBottom: 0 }}>Desconto ao cliente?</label>
          <button
            type="button"
            onClick={() => setForm(f => ({ ...f, tem_desconto: !f.tem_desconto, desconto_cliente: f.tem_desconto ? 0 : f.desconto_cliente }))}
            style={{ ...btnGhost, padding: "4px 12px", background: form.tem_desconto ? "rgba(184,147,90,0.15)" : "transparent", color: form.tem_desconto ? gold : ink3, borderColor: form.tem_desconto ? gold : line }}
            data-testid="toggle-desconto"
          >
            {form.tem_desconto ? "SIM" : "NÃO"}
          </button>
        </div>
        {form.tem_desconto && (
          <div style={{ marginTop: 8, maxWidth: 260 }}>
            <input type="number" min="0" step="0.01" value={form.desconto_cliente} onChange={e => setForm(f => ({ ...f, desconto_cliente: e.target.value }))} style={inp} placeholder="R$ 0,00" data-testid="form-desconto" />
          </div>
        )}

        {/* Totais */}
        <div style={{ background: bg1, border: `1px solid ${line}`, borderRadius: 4, padding: 14, marginTop: 16 }} data-testid="totais-preview">
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ color: ink3, fontSize: 12 }}>Total sinistro (custo + desconto − absorção):</span>
            <span style={{ color: gold, fontFamily: "Fraunces, serif", fontSize: 22 }} data-testid="total-sinistro">{brl(totalSinistro)}</span>
          </div>
          {previewSinistros.length > 0 && (
            <div style={{ marginTop: 10, borderTop: `1px dashed ${line}`, paddingTop: 10 }}>
              <div style={{ fontSize: 10, color: ink3, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>Divisão prevista</div>
              {previewSinistros.map((p, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: ink2, padding: "3px 0" }} data-testid={`preview-sinistro-${i}`}>
                  <span>{p.nome} ({p.pct}%)</span>
                  <span style={{ color: ink, fontWeight: 600 }}>{brl(p.valor)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      {isEdit && (
        <div style={{ marginTop: 12 }}>
          <label style={lbl}>Motivo da alteração *</label>
          <textarea value={motivo} onChange={e => setMotivo(e.target.value)} style={{ ...inp, minHeight: 60 }} placeholder="Explique o porquê da mudança..." data-testid="form-motivo" />
        </div>
      )}

      <div style={{ marginTop: 22, display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button onClick={onClose} style={btnGhost} data-testid="btn-cancelar">Cancelar</button>
        <button onClick={submit} style={btnPrimary} disabled={saving || !canSubmit} data-testid="btn-salvar">
          {saving ? "Salvando…" : (isEdit ? "Salvar alterações" : "Registrar")}
        </button>
      </div>
    </Modal>
  );
}

/* --------------------- DETALHE --------------------- */

function DetalheOcorrencia({ id, onClose, onChanged, headers, obras }) {
  const [oc, setOc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [descontoIdx, setDescontoIdx] = useState(null); // qual sinistro está descontando

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ocorrencias/${id}`, { headers });
      setOc(r.data);
    } catch (e) { toastError(pickErrorMsg(e)); }
    finally { setLoading(false); }
  }, [id, headers]);
  useEffect(() => { load(); }, [load]);

  const alterarStatusSinistro = async (idx, status) => {
    try {
      await axios.put(`${API}/ocorrencias/${id}/sinistro/${idx}/status`, { status }, { headers });
      toastSuccess("Status atualizado.");
      load(); onChanged();
    } catch (e) { toastError(pickErrorMsg(e)); }
  };

  const excluir = async () => {
    if (!window.confirm("Excluir esta ocorrência? Os valores dela saem do Lucro Real da obra.")) return;
    try {
      await axios.delete(`${API}/ocorrencias/${id}`, { headers });
      toastSuccess("Ocorrência excluída.");
      onChanged();
      onClose();
    } catch (e) { toastError(pickErrorMsg(e)); }
  };

  if (editMode && oc) {
    return (
      <FormOcorrencia
        obras={obras}
        fixedObraId={null}
        initial={oc}
        onClose={() => setEditMode(false)}
        onSaved={() => { setEditMode(false); load(); onChanged(); }}
        headers={headers}
      />
    );
  }

  return (
    <Modal onClose={onClose} title="Detalhes da Ocorrência" testid="modal-detalhe" wide>
      {loading || !oc ? (
        <div style={{ padding: 40, textAlign: "center", color: ink3 }}>Carregando…</div>
      ) : (
        <>
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18, gap: 12 }}>
            <div>
              <div style={{ fontSize: 10, color: ink3, letterSpacing: 1.5, textTransform: "uppercase" }}>
                {fmtDataBR(oc.data_ocorrencia)} · Pedido {oc.numero_pedido || "—"}
              </div>
              <div style={{ marginTop: 4 }}>
                <StatusPill status={oc.status} map={STATUS_OC} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setEditMode(true)} style={btnGhost} data-testid="btn-editar-ocorrencia">
                <Edit3 size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />
                Editar
              </button>
              <button onClick={excluir} style={{ ...btnGhost, color: "#c47a7a" }} data-testid="btn-excluir-ocorrencia">
                <Trash2 size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />
                Excluir
              </button>
            </div>
          </div>

          {/* Descrição */}
          <Section num={1} title="O que aconteceu">
            <p style={{ margin: "0 0 12px", color: ink2, lineHeight: 1.6 }}>{oc.descricao}</p>
            {oc.material && <div style={{ fontSize: 12, color: ink3 }}>Material: <strong style={{ color: ink }}>{oc.material}</strong></div>}
            {(oc.fotos || []).length > 0 && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                {oc.fotos.map((url, i) => (
                  <a key={i} href={url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url} target="_blank" rel="noreferrer">
                    <img src={url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url} alt="" style={{ width: 100, height: 100, objectFit: "cover", borderRadius: 3, border: `1px solid ${line}` }} />
                  </a>
                ))}
              </div>
            )}
          </Section>

          {/* Responsáveis */}
          <Section num={2} title="Responsáveis">
            <div style={{ display: "grid", gap: 6 }}>
              {(oc.responsaveis || []).map((r, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 12px", background: bg1, borderRadius: 3, fontSize: 13 }}>
                  <span>
                    <strong style={{ color: ink }}>{r.responsavel_nome}</strong>
                    <span style={{ color: ink3, marginLeft: 8 }}>({RESP_TIPOS.find(t => t.v === r.responsavel_tipo)?.label || r.responsavel_tipo})</span>
                    {!r.eh_terceiro && <span style={{ marginLeft: 8, fontSize: 10, background: "rgba(184,147,90,0.15)", color: gold, padding: "2px 6px", borderRadius: 2, letterSpacing: 1 }}>CAMASA</span>}
                  </span>
                  <span style={{ color: gold, fontWeight: 600 }}>{r.percentual_culpa}%</span>
                </div>
              ))}
            </div>
          </Section>

          {/* Financeiro (só se admin/gestor/financeiro) */}
          {oc.total_sinistro != null && (
            <Section num={3} title="Impacto financeiro">
              <div style={grid2}>
                <MetricRow label="Custo de correção" value={brl(oc.custo_correcao)} />
                <MetricRow label="Desconto ao cliente" value={brl(oc.desconto_cliente)} />
                <MetricRow label="Camasa absorve" value={brl(oc.camasa_absorve)} />
                <MetricRow label="Total sinistro" value={brl(oc.total_sinistro)} bold />
              </div>
            </Section>
          )}

          {/* Sinistros */}
          {(oc.sinistros || []).length > 0 && (
            <Section num={4} title="Sinistros (terceiros)">
              {(oc.sinistros || []).map((s, i) => (
                <div key={i} style={{ background: bg1, border: `1px solid ${line}`, borderRadius: 4, padding: 14, marginBottom: 10 }} data-testid={`sinistro-${i}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 13, color: ink, fontWeight: 600 }}>{s.responsavel_nome}</div>
                      <div style={{ fontSize: 11, color: ink3, marginTop: 2 }}>{s.percentual_culpa}% de culpa</div>
                    </div>
                    <StatusPill status={s.status} map={STATUS_SIN} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 10, color: ink3, letterSpacing: 1, textTransform: "uppercase" }}>Valor total</div>
                      <div style={{ fontSize: 15, color: ink, fontFamily: "Fraunces, serif" }}>{brl(s.valor_sinistro)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: ink3, letterSpacing: 1, textTransform: "uppercase" }}>Descontado</div>
                      <div style={{ fontSize: 15, color: ink3, fontFamily: "Fraunces, serif" }}>{brl(s.valor_descontado)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: ink3, letterSpacing: 1, textTransform: "uppercase" }}>Saldo devedor</div>
                      <div style={{ fontSize: 15, color: s.saldo_devedor > 0 ? danger : "#7da882", fontFamily: "Fraunces, serif", fontWeight: 600 }}>
                        {brl(s.saldo_devedor)}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {Object.entries(STATUS_SIN).filter(([k]) => k !== s.status).map(([k, v]) => (
                      <button key={k} onClick={() => alterarStatusSinistro(i, k)} style={{ ...btnGhost, padding: "4px 10px", fontSize: 10, color: v.color, borderColor: v.color }} data-testid={`btn-status-${i}-${k}`}>
                        → {v.label}
                      </button>
                    ))}
                    {s.saldo_devedor > 0 && (
                      <button onClick={() => setDescontoIdx(i)} style={{ ...btnPrimary, padding: "4px 12px", fontSize: 10 }} data-testid={`btn-desconto-${i}`}>
                        Registrar desconto
                      </button>
                    )}
                  </div>
                  {(s.historico_alteracoes || []).length > 0 && (
                    <details style={{ marginTop: 10 }}>
                      <summary style={{ fontSize: 11, color: ink3, cursor: "pointer" }}>
                        <History size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />
                        Histórico ({s.historico_alteracoes.length})
                      </summary>
                      <div style={{ marginTop: 6, fontSize: 11, color: ink3 }}>
                        {s.historico_alteracoes.map((h, hi) => (
                          <div key={hi} style={{ padding: "4px 0", borderTop: `1px dashed ${line}` }}>
                            <strong>{h.campo}</strong>: {String(h.valor_anterior ?? "—")} → {String(h.valor_novo ?? "—")}
                            <div style={{ fontSize: 10, color: ink3 }}>{fmtDataBR(h.data)} · {h.usuario} · {h.motivo}</div>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              ))}
            </Section>
          )}

          {/* Histórico raiz */}
          {(oc.historico || []).length > 0 && (
            <Section num={5} title={`Histórico de Alterações (${oc.historico.length})`}>
              {oc.historico.map((h, i) => (
                <div key={i} style={{ fontSize: 12, color: ink3, padding: "6px 0", borderTop: i > 0 ? `1px dashed ${line}` : "none" }} data-testid={`hist-${i}`}>
                  <span style={{ color: ink }}><strong>{h.campo}</strong></span>: {JSON.stringify(h.valor_anterior).slice(0, 60)} → {JSON.stringify(h.valor_novo).slice(0, 60)}
                  <div style={{ fontSize: 10, marginTop: 2 }}>{fmtDataBR(h.data)} · {h.usuario} · {h.motivo || "sem motivo"}</div>
                </div>
              ))}
            </Section>
          )}
        </>
      )}

      {descontoIdx != null && oc && (
        <DescontoModal
          sinistro={oc.sinistros[descontoIdx]}
          onClose={() => setDescontoIdx(null)}
          onDone={() => { setDescontoIdx(null); load(); onChanged(); }}
          onSave={async ({ valor, motivo, referencia }) => {
            await axios.put(`${API}/ocorrencias/${id}/sinistro/${descontoIdx}/desconto`, { valor, motivo, referencia }, { headers });
          }}
        />
      )}
    </Modal>
  );
}

function DescontoModal({ sinistro, onClose, onDone, onSave }) {
  const [valor, setValor] = useState("");
  const [motivo, setMotivo] = useState("");
  const [ref, setRef] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await onSave({ valor: Number(valor) || 0, motivo, referencia: ref });
      toastSuccess("Desconto registrado.");
      onDone();
    } catch (e) { toastError(pickErrorMsg(e)); }
    finally { setSaving(false); }
  };
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 1002,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }} data-testid="modal-desconto">
      <div style={{ background: bg2, border: `1px solid ${line}`, padding: 24, borderRadius: 4, width: 420, maxWidth: "100%" }}>
        <h3 style={{ margin: "0 0 12px", color: ink, fontFamily: "Fraunces, serif" }}>Registrar desconto</h3>
        <div style={{ fontSize: 12, color: ink3, marginBottom: 14 }}>
          Saldo devedor de <strong style={{ color: ink }}>{sinistro.responsavel_nome}</strong>: <strong style={{ color: danger }}>{brl(sinistro.saldo_devedor)}</strong>
        </div>
        <label style={lbl}>Valor a descontar (R$) *</label>
        <input type="number" min="0.01" max={sinistro.saldo_devedor} step="0.01" value={valor} onChange={e => setValor(e.target.value)} style={inp} data-testid="desconto-valor" />
        <div style={{ height: 10 }} />
        <label style={lbl}>Referência (opcional)</label>
        <input value={ref} onChange={e => setRef(e.target.value)} style={inp} placeholder="Ex: pagamento serviço #123" data-testid="desconto-ref" />
        <div style={{ height: 10 }} />
        <label style={lbl}>Motivo</label>
        <textarea value={motivo} onChange={e => setMotivo(e.target.value)} style={{ ...inp, minHeight: 60 }} data-testid="desconto-motivo" />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={btnGhost}>Cancelar</button>
          <button onClick={submit} style={btnPrimary} disabled={saving || !valor || Number(valor) <= 0} data-testid="desconto-submit">
            {saving ? "Salvando…" : "Confirmar"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* --------------------- Common UI --------------------- */

const grid2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 };

function Section({ num, title, children }) {
  return (
    <section style={{ marginTop: 18, marginBottom: 8 }}>
      <div style={{ fontSize: 10, color: ink3, letterSpacing: 2, textTransform: "uppercase", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ width: 22, height: 22, borderRadius: "50%", background: gold, color: bg1, fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{num}</span>
        {title}
      </div>
      <div>{children}</div>
    </section>
  );
}

function MetricRow({ label, value, bold }) {
  return (
    <div style={{ padding: "10px 12px", background: bg1, borderRadius: 3, border: `1px solid ${line}` }}>
      <div style={{ fontSize: 10, color: ink3, letterSpacing: 1.2, textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: bold ? 20 : 15, color: bold ? gold : ink, fontFamily: "Fraunces, serif", marginTop: 2, fontWeight: bold ? 600 : 400 }}>{value}</div>
    </div>
  );
}

function Modal({ children, onClose, title, testid, wide }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1001,
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      padding: 30, overflowY: "auto",
    }} onClick={onClose} data-testid={testid}>
      <div
        style={{
          background: bg2, border: `1px solid ${line}`, padding: 26, borderRadius: 4,
          width: wide ? 760 : 620, maxWidth: "100%",
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <h2 style={{ margin: 0, color: ink, fontFamily: "Fraunces, serif", fontSize: 22 }}>{title}</h2>
          <button onClick={onClose} style={{ background: "none", color: ink3, border: "none", cursor: "pointer", padding: 4 }} data-testid="modal-close">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
